import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, collection, query, getDocs, orderBy, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { readingTests, speakingTests, veptTests } from '../data/mockTests';
import { FaLock, FaUnlock } from 'react-icons/fa';
import Header from '../components/Header';
import { trackGA4Event, getGAClientId } from '../utils/GA4Analytics';

import {
  DashboardNav,
  QuickStats,
  PlanStatus,
  SkillBreakdown,
  AccountDetails,
  AvailableTests,
  TestHistory
} from '../components/DashboardComponents';

const Dashboard = () => {
  const navigate = useNavigate();
  const [userTestAccess, setUserTestAccess] = useState(true); // Default to true now
  const [attemptedTests, setAttemptedTests] = useState(new Set());
  const [currency, setCurrency] = useState('INR');

  useEffect(() => {
    const getDetectedCurrency = () => {
      const country = sessionStorage.getItem('ga4_user_country');
      const browserLang = navigator.language || '';
      const sessionLocale = sessionStorage.getItem('versant_locale');
      
      if (window.location.pathname.startsWith('/es') || country === 'Spain' || browserLang.startsWith('es') || sessionLocale === 'es') {
        return 'EUR';
      }
      const highPayingUSDCounteries = ['United States', 'United Kingdom', 'Canada', 'Japan', 'Singapore', 'Australia', 'Brazil', 'South Korea'];
      if (highPayingUSDCounteries.includes(country)) {
        return 'USD';
      }
      if (country === 'India') {
        return 'INR';
      }
      return 'USD';
    };
    setCurrency(getDetectedCurrency());
  }, []);

  // Ebook Access State
  const [hasEbookAccess, setHasEbookAccess] = useState(false);
  const [ebookPurchasedAt, setEbookPurchasedAt] = useState(null);
  const [isProcessingEbook, setIsProcessingEbook] = useState(false);

  // User Profile Stats State
  const [testHistory, setTestHistory] = useState([]);
  const [stats, setStats] = useState({
    totalTests: 0,
    averageScore: 0,
    highestScore: 0
  });

  const docDataRef = React.useRef(null);

  // Derived state for Skill Breakdown
  const [skillBreakdown, setSkillBreakdown] = useState({
    reading: 0,
    writing: 0,
    speaking: 0,
    listening: 0
  });

  const [studentData, setStudentData] = useState({
    name: 'User',
    email: 'user@example.com',
    plan: 'Basic',
    // ... maps to state
  });

  useEffect(() => {
    let unsubscribeUserDoc = null;
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      const userRef = doc(db, 'users', currentUser.uid);

      // Update Basic Info
      setStudentData(prev => ({
        ...prev,
        name: currentUser.displayName || currentUser.email.split('@')[0],
        email: currentUser.email
      }));

      // Helper to load and calculate all stats from both userDoc and testResults subcollection
      const loadUserDashboardData = async (providedUserData = null) => {
        try {
          // 1. Fetch user document directly if not provided
          let currentUserDocData = providedUserData;
          if (!currentUserDocData) {
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              currentUserDocData = userSnap.data();
            }
          }

          // 2. Fetch testResults subcollection
          const resultsRef = collection(db, 'users', currentUser.uid, 'testResults');
          const querySnapshot = await getDocs(resultsRef);
          const rawDocs = [];
          querySnapshot.forEach((d) => {
            rawDocs.push({ id: d.id, ...d.data() });
          });

          // Sort in memory (handles timestamp, createdAt, or date safely)
          rawDocs.sort((a, b) => {
            const timeA = new Date(a.timestamp || a.createdAt?.toDate?.() || a.createdAt || 0).getTime();
            const timeB = new Date(b.timestamp || b.createdAt?.toDate?.() || b.createdAt || 0).getTime();
            return timeB - timeA;
          });

          const attempts = new Set();
          const history = [];

          let totalScoreSum = 0;
          let maxScore = 0;
          let count = 0;

          // Skill aggregation variables
          let typingSum = 0, typingCount = 0;
          let sentenceSum = 0, sentenceCount = 0;
          let fillSum = 0, fillCount = 0;
          let jumbledSum = 0, jumbledCount = 0;

          rawDocs.forEach((data) => {
            // Deduplication Logic
            const isDuplicate = history.some(h =>
              h.testId === data.testId &&
              h.totalScore === data.totalScore &&
              Math.abs(new Date(h.timestamp || h.createdAt) - new Date(data.timestamp || data.createdAt)) < 60000 * 5
            );

            if (!isDuplicate) {
              history.push({ ...data });
              if (data.testId) attempts.add(data.testId.toString());

              if (data.type !== undefined || data.totalScore !== undefined || data.score !== undefined) {
                count++;
                let score = data.totalScore !== undefined ? data.totalScore : (data.score || 0);

                if (score > 80 && score <= 630 && data.type?.includes('speaking')) {
                  score = Math.round(20 + (score / 630) * 60);
                } else if (score > 80) {
                  score = 80;
                }

                totalScoreSum += score;
                if (score > maxScore) maxScore = score;

                if (data.sections) {
                  data.sections.forEach(sec => {
                    if (sec.section === 'Typing') {
                      typingSum += (sec.score || 0); typingCount++;
                    } else if (sec.section === 'Sentence Completion') {
                      sentenceSum += (sec.score || 0); sentenceCount++;
                    } else if (sec.section === 'Fill in the Blanks') {
                      fillSum += (sec.score || 0); fillCount++;
                    } else if (sec.section === 'Jumbled Words') {
                      jumbledSum += (sec.score || 0); jumbledCount++;
                    }
                  });
                }
              }
            }
          });

          // Fallback if subcollection is empty: Read from user document fields
          if (count === 0 && currentUserDocData && (currentUserDocData.testsCompleted > 0 || currentUserDocData.lastScore > 0)) {
            const userLastScore = currentUserDocData.lastScore || 0;
            const userTestsCount = currentUserDocData.testsCompleted || 1;
            count = userTestsCount;
            maxScore = userLastScore;
            totalScoreSum = userLastScore * userTestsCount;

            const lastTime = currentUserDocData.lastActivity?.toDate ? currentUserDocData.lastActivity.toDate().toISOString() : (currentUserDocData.lastActivity || new Date().toISOString());

            for (let i = 0; i < Math.min(userTestsCount, 5); i++) {
              attempts.add((i + 1).toString());
              history.push({
                id: `history_${i + 1}`,
                testId: i + 1,
                type: 'full_test',
                totalScore: userLastScore,
                timestamp: lastTime
              });
            }
          }

          setAttemptedTests(attempts);
          setTestHistory(history);
          setStats({
            totalTests: count,
            averageScore: count > 0 ? Math.round(totalScoreSum / count) : 0,
            highestScore: maxScore
          });

          // Calculate approximate skill breakdown (normalized to 80 scale for visualization)
          const hasSectionScores = typingCount > 0 || sentenceCount > 0 || fillCount > 0 || jumbledCount > 0;
          if (hasSectionScores) {
            setSkillBreakdown({
              writing: Math.min(80, Math.round((typingSum / (typingCount || 1)) + (jumbledSum / (jumbledCount || 1)))),
              reading: Math.min(80, Math.round((fillSum / (fillCount || 1)) * 4)),
              speaking: Math.min(80, Math.round((sentenceSum / (sentenceCount || 1)) * 4)),
              listening: Math.min(80, Math.round((sentenceSum / (sentenceCount || 1)) * 4))
            });
          } else if (maxScore > 0) {
            setSkillBreakdown({
              writing: maxScore,
              reading: maxScore,
              speaking: maxScore,
              listening: maxScore
            });
          }

        } catch (error) {
          console.error("Error fetching dashboard data:", error);
        }
      };

      // Realtime listener for user document changes
      if (unsubscribeUserDoc) unsubscribeUserDoc();
      unsubscribeUserDoc = onSnapshot(userRef, async (userDoc) => {
        if (userDoc.exists()) {
          const data = userDoc.data();
          docDataRef.current = data;
          
          if (!data.email) {
            try {
              await updateDoc(userRef, { email: currentUser.email, uid: currentUser.uid });
            } catch (e) {
              console.error('Error syncing email:', e);
            }
          }

          const rawPremiumAccess = data.paidTests === true || data.hasPaid === true || data.isPremium === true;
          
          let isExpired = false;
          if (data.premiumExpiresAt) {
            try {
              const expiryDate = data.premiumExpiresAt.toDate ? data.premiumExpiresAt.toDate() : new Date(data.premiumExpiresAt);
              if (new Date() > expiryDate) {
                isExpired = true;
              }
            } catch (e) {
              console.error("Error parsing premiumExpiresAt date:", e);
            }
          }

          const hasPremiumAccess = rawPremiumAccess && !isExpired;
          setUserTestAccess(hasPremiumAccess);
          setStudentData(prev => ({ 
            ...prev, 
            plan: hasPremiumAccess ? (data.plan === '45_Day_Pass' ? '45-Day Pass' : 'Premium VIP') : (isExpired ? 'Expired' : 'Free') 
          }));

          const isEbookUnlocked = data.hasPurchasedEbook === true;
          setHasEbookAccess(isEbookUnlocked);
          if (data.ebookPurchasedAt) {
            setEbookPurchasedAt(data.ebookPurchasedAt.toDate());
          } else if (hasPremiumAccess && data.paidAt) {
            setEbookPurchasedAt(data.paidAt.toDate());
          } else {
            setEbookPurchasedAt(null);
          }

          // Trigger dashboard data recalculation with latest user document data
          loadUserDashboardData(data);
        } else {
          try {
            await setDoc(userRef, { 
              paidTests: false,
              hasPaid: false,
              email: currentUser.email,
              uid: currentUser.uid,
              hasPurchasedEbook: false
            });
          } catch (e) {
            console.error('Error creating user doc:', e);
          }
          setUserTestAccess(false);
          setHasEbookAccess(false);
        }
      });

      // Synchronously launch initial dashboard data load
      loadUserDashboardData();
    });
    return () => {
      if (unsubscribeUserDoc) unsubscribeUserDoc();
      unsubscribe();
    };
  }, [navigate]);

  const userTestStatus = Array.from({ length: 20 }, (_, index) => {
    const testId = (index + 1).toString();
    return {
      id: index + 1,
      name: `Test ${index + 1}`,
      paid: true,
      attempted: attemptedTests.has(testId),
      description: `Test ${index + 1} challenges your English skills...`,
      level: index + 1 <= 3 ? 'Easy' : index + 1 <= 8 ? 'Moderate' : 'Advanced',
      duration: '35 mins'
    };
  });

  const handleStartTest = (test) => {
    navigate('/versant-reading-and-writing-mock-test/start', { state: { testId: test.id } });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error(error);
    }
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleBuyEbook = async () => {
    const currentPrice = currency === 'INR' ? 199 : currency === 'EUR' ? 299 : 299;
    trackGA4Event('ebook_buy_click', { product_name: 'Versant Test Mastery Ebook', payment_amount: currentPrice, currency: currency });
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    setIsProcessingEbook(true);
    const res = await loadRazorpay();
    if (!res) {
      alert('Razorpay failed to load. Please check your internet connection.');
      setIsProcessingEbook(false);
      return;
    }

    try {
      const orderResponse = await fetch('/.netlify/functions/createRazorpayOrder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: currentUser.uid,
          email: currentUser.email,
          productType: 'ebook',
          currency: currency,
          referredBy: localStorage.getItem('versant_affiliate_ref') || null,
          gaClientId: getGAClientId()
        })
      });

      if (!orderResponse.ok) {
        throw new Error("Failed to create order on server.");
      }

      const orderData = await orderResponse.json();

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "VersantPro Premium Ebook",
        description: "Ultimate Crack Guide to Versant Test",
        image: "https://versantpro.com/logo.png",
        order_id: orderData.order_id,
        prefill: {
          email: currentUser.email,
          name: currentUser.displayName || ''
        },
        theme: {
          color: "#0f766e"
        },
        modal: {
          ondismiss: function() {
            trackGA4Event('razorpay_payment_failed', { product_name: 'Versant Test Mastery Ebook', payment_amount: currentPrice, currency: currency, reason: 'User dismissed checkout modal' });
          }
        },
        handler: async function (response) {
          setIsProcessingEbook(true);
          try {
            const verifyRes = await fetch('/.netlify/functions/verifyRazorpayPayment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                uid: currentUser.uid,
                email: currentUser.email,
                productType: 'ebook',
                referredBy: localStorage.getItem('versant_affiliate_ref') || null
              })
            });

             if (verifyRes.ok) {
               trackGA4Event('razorpay_payment_success', {
                 transaction_id: response.razorpay_payment_id,
                 payment_amount: currentPrice,
                 currency: currency,
                 product_name: 'Versant Test Mastery Ebook',
                 payment_method: 'razorpay'
               });
               alert("Payment Verified! Your ebook has been unlocked.");
              setHasEbookAccess(true);
            } else {
              alert("Payment verification failed.");
            }
          } catch (err) {
            console.error("Verification query error:", err);
            alert("Network error. If money was deducted, contact support.");
          } finally {
            setIsProcessingEbook(false);
          }
        }
      };

      trackGA4Event('razorpay_checkout_open', { product_name: 'Versant Test Mastery Ebook', payment_amount: currentPrice, currency: currency });
      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

    } catch (err) {
      console.error(err);
      alert("Failed to initialize checkout.");
    } finally {
      setIsProcessingEbook(false);
    }
  };

  const handleDownloadEbook = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      trackGA4Event('ebook_download', { email: currentUser.email });
      const token = await currentUser.getIdToken(true);
      window.location.href = `/.netlify/functions/downloadEbook?token=${token}`;
    } catch (err) {
      console.error("Download token generation failed:", err);
      alert("Error starting ebook download.");
    }
  };

  // Combine real stats into studentData object for the view
  const dashboardData = {
    ...studentData,
    testsAttempted: stats.totalTests,
    currentScore: stats.highestScore,
    averageScore: stats.averageScore,
    testsRemaining: 20 - stats.totalTests, // Assuming 20 total tests
    tests: userTestStatus, // Pass this to AvailableTests
  };

  return (
    <div className="h-full w-full overflow-auto bg-gray-50 min-h-screen">
      <Header page="dashboard" />
      <DashboardNav studentData={dashboardData} onLogout={handleLogout} />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-10 animate-fade-in-down">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-2">
            Welcome back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">{dashboardData.name.split(' ')[0]}</span>! 👋
          </h1>
          <p className="text-gray-600 text-lg">Your progress dashboard. Choose a module to start practicing.</p>
        </div>

        {/* 🔥 Premium Upsell Banner — shown only to free users */}
        {!userTestAccess && (
          <div className="relative mb-10 overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-900 via-green-800 to-teal-900 p-6 shadow-2xl border border-emerald-500/30">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-left">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2.5 h-2.5 bg-red-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(248,113,113,0.8)]"></span>
                  <span className="text-red-300 text-xs font-bold uppercase tracking-widest">⚠ You are on the Free Plan</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-2 tracking-tight">
                  Only 1 of 20 tests unlocked.
                </h2>
                <p className="text-emerald-200/80 text-sm max-w-xl">
                  Top MNCs like Amazon & Deloitte require a score of 65+. Most students only reach that score after 4-6 full practice tests. You have access to just 1.
                </p>
                <div className="flex flex-wrap items-center gap-4 mt-4">
                  <div className="flex items-center gap-1.5 text-emerald-300 text-sm font-semibold">
                    <span>✓</span> 38 Total Premium Tests (19 R&W + 19 Speaking)
                  </div>
                  <div className="flex items-center gap-1.5 text-emerald-300 text-sm font-semibold">
                    <span>✓</span> Deep AI Score Analysis
                  </div>
                  <div className="flex items-center gap-1.5 text-emerald-300 text-sm font-semibold">
                    <span>✓</span> Lifetime Access
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 text-center">
                <div className="text-emerald-300/60 text-xs line-through mb-1">
                  {currency === 'INR' ? '₹2,999' : currency === 'EUR' ? '€29.99' : '$29.99'}
                </div>
                <div className="text-4xl font-black text-white mb-1">
                  {currency === 'INR' ? '₹1,449' : currency === 'EUR' ? '€14.99' : '$14.99'}
                </div>
                <div className="text-emerald-300 text-xs mb-4">One-time · No subscription</div>
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-8 py-3.5 bg-gradient-to-r from-emerald-400 to-teal-400 hover:from-emerald-300 hover:to-teal-300 text-slate-900 font-extrabold rounded-xl shadow-[0_0_20px_rgba(52,211,153,0.4)] hover:shadow-[0_0_30px_rgba(52,211,153,0.6)] transition-all active:scale-95 text-sm"
                >
                  🚀 Unlock All 20 Tests
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Stats Grid */}
        <QuickStats studentData={dashboardData} />

        {/* 📘 Premium Ebook Download / Purchase Card */}
        <div className="mb-10 bg-white rounded-2xl border-2 border-emerald-50/50 p-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6 hover:border-teal-500/30 transition-all duration-300">
          <div className="flex items-center gap-4 text-left">
            <div className="w-16 h-16 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center text-3xl shadow-inner flex-shrink-0 select-none">
              📘
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-xl font-bold text-gray-900">Versant Test Mastery</h3>
                {hasEbookAccess ? (
                  <span className="px-2.5 py-0.5 bg-emerald-100 border border-emerald-200 text-emerald-800 text-[10px] font-bold uppercase rounded-full tracking-wider">
                    Purchased
                  </span>
                ) : (
                  <span className="px-2.5 py-0.5 bg-teal-50 border border-teal-200 text-teal-700 text-[10px] font-bold uppercase rounded-full tracking-wider">
                    {currency === 'INR' ? '₹199' : currency === 'EUR' ? '€2.99' : '$2.99'}
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-sm mt-1.5 max-w-xl leading-relaxed">
                Versant Test Mastery- A Complete Guide with 20 practice Sets. Contains speaking templates, acoustic mic guidelines, and sample responses.
              </p>
              {hasEbookAccess && ebookPurchasedAt && (
                <p className="text-gray-400 text-xs mt-1.5 font-medium">
                  Unlocked on {ebookPurchasedAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 w-full md:w-auto">
            {hasEbookAccess ? (
              <button
                onClick={handleDownloadEbook}
                className="w-full md:w-auto px-6 py-3.5 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white font-bold rounded-xl shadow-md active:scale-95 transition flex items-center justify-center gap-2 whitespace-nowrap"
              >
                📥 Download Ebook
              </button>
            ) : (
              <button
                onClick={handleBuyEbook}
                disabled={isProcessingEbook}
                className="w-full md:w-auto px-6 py-3.5 bg-gradient-to-r from-teal-500 to-emerald-400 hover:from-teal-400 hover:to-emerald-300 text-slate-900 font-extrabold rounded-xl shadow-md active:scale-95 transition flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-50"
              >
                {isProcessingEbook ? "Launching checkout..." : `Buy Ebook (${currency === 'INR' ? '₹199' : currency === 'EUR' ? '€2.99' : '$2.99'})`}
              </button>
            )}
          </div>
        </div>

        {/* 🎯 Section-wise Practice Hub Card */}
        <div className="mb-10 bg-white rounded-2xl border-2 border-emerald-50/50 p-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6 hover:border-teal-500/30 transition-all duration-300">
          <div className="flex items-center gap-4 text-left">
            <div className="w-16 h-16 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-3xl shadow-inner flex-shrink-0 select-none">
              🎯
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-xl font-bold text-gray-900">Section-wise Practice Hub</h3>
                <span className="px-2.5 py-0.5 bg-indigo-50 border border-indigo-100 text-indigo-700 text-[10px] font-bold uppercase rounded-full tracking-wider">
                  New Feature
                </span>
              </div>
              <p className="text-gray-500 text-sm mt-1.5 max-w-xl leading-relaxed">
                Struggling with a specific section like Repeat Sentences or Email Writing? Practice individual test sections with 20 sets each and unlock only what you need.
              </p>
            </div>
          </div>
          <div className="flex-shrink-0 w-full md:w-auto">
            <button
              onClick={() => navigate('/versant-practice-hub')}
              className="w-full md:w-auto px-6 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold rounded-xl shadow-md active:scale-95 transition flex items-center justify-center gap-2 whitespace-nowrap"
            >
              🚀 Open Practice Hub
            </button>
          </div>
        </div>

        {/* Reading & Writing Section */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-emerald-100 text-emerald-600 rounded-xl">
              <span role="img" aria-label="pen" className="text-2xl">✍️</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Reading & Writing</h2>
              <p className="text-gray-500">Master grammar, vocabulary, and composition with 20 mock tests.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {readingTests.map((test) => {
              const isLocked = !userTestAccess && test.id > 1;

              return (
                <div
                  key={test.id}
                  onClick={() => {
                    if (!isLocked) navigate('/versant-reading-and-writing-mock-test/start', { state: { testId: test.id } });
                    else navigate('/pricing');
                  }}
                  className={`relative group bg-white rounded-xl p-4 border-2 transition-all duration-200 cursor-pointer ${isLocked
                    ? 'border-gray-100 opacity-60 grayscale-[0.8] hover:opacity-100 hover:grayscale-0'
                    : 'border-emerald-50 hover:border-emerald-500 hover:shadow-lg hover:-translate-y-1'
                    }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm font-bold text-gray-900">{test.title}</span>
                    {isLocked ? (
                      <FaLock className="text-gray-400 text-xs" />
                    ) : (
                      <FaUnlock className="text-emerald-500 text-xs" />
                    )}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-3">
                    <span>⏱ {test.duration}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${test.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                      test.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                      {test.difficulty}
                    </span>
                  </div>

                  {!userTestAccess && test.id === 1 && (
                    <div className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md animate-bounce">
                      FREE
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Speaking & Listening Section */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-blue-100 text-blue-600 rounded-xl">
              <span role="img" aria-label="mic" className="text-2xl">🎙️</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Speaking & Listening</h2>
              <p className="text-gray-500">Enhance pronunciation and fluency with 20 AI-graded assessments.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {speakingTests.map((test) => {
              const isLocked = !userTestAccess && test.id > 1;

              return (
                <div
                  key={test.id}
                  onClick={() => {
                    if (!isLocked) navigate('/versant-speaking-and-listening-practice-test/start/full', { state: { testId: test.id } });
                    else navigate('/pricing');
                  }}
                  className={`relative group bg-white rounded-xl p-4 border-2 transition-all duration-200 cursor-pointer ${isLocked
                    ? 'border-gray-100 opacity-60 grayscale-[0.8] hover:opacity-100 hover:grayscale-0'
                    : 'border-blue-50 hover:border-blue-500 hover:shadow-lg hover:-translate-y-1'
                    }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm font-bold text-gray-900">{test.title}</span>
                    {isLocked ? (
                      <FaLock className="text-gray-400 text-xs" />
                    ) : (
                      <FaUnlock className="text-blue-500 text-xs" />
                    )}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-3">
                    <span>⏱ {test.duration}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${test.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                      test.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                      {test.difficulty}
                    </span>
                  </div>

                  {!userTestAccess && test.id === 1 && (
                    <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md animate-bounce">
                      FREE
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* VEPT Academic Placement Section */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-indigo-100 text-indigo-600 rounded-xl">
              <span role="img" aria-label="mortar-board" className="text-2xl">🎓</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">VEPT Academic Placement</h2>
              <p className="text-gray-500">Practice the full 9-part unified Versant English Placement Test (Listening, Speaking, Reading, Writing).</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {veptTests.map((test) => {
              const isLocked = !userTestAccess && test.id > 1;

              return (
                <div
                  key={test.id}
                  onClick={() => {
                    if (!isLocked) navigate(`/versant-vept-mock-test/start/${test.id}`);
                    else navigate('/pricing');
                  }}
                  className={`relative group bg-white rounded-xl p-4 border-2 transition-all duration-200 cursor-pointer ${isLocked
                    ? 'border-gray-100 opacity-60 grayscale-[0.8] hover:opacity-100 hover:grayscale-0'
                    : 'border-indigo-50 hover:border-indigo-500 hover:shadow-lg hover:-translate-y-1'
                    }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm font-bold text-gray-900">{test.title}</span>
                    {isLocked ? (
                      <FaLock className="text-gray-400 text-xs" />
                    ) : (
                      <FaUnlock className="text-indigo-500 text-xs" />
                    )}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-3">
                    <span>⏱ {test.duration}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${test.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                      test.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                      {test.difficulty}
                    </span>
                  </div>

                  {!userTestAccess && test.id === 1 && (
                    <div className="absolute -top-2 -right-2 bg-indigo-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md animate-bounce">
                      FREE
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Analytics & History Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          <SkillBreakdown skillBreakdown={skillBreakdown} />
          <div className="lg:col-span-1">
            <AccountDetails studentData={dashboardData} />
          </div>
        </div>

        <div className="mt-8">
          <TestHistory completedTests={testHistory} />
        </div>
      </main>
    </div>
  );
};

export default Dashboard;